/**
 * FOLLOWUP-01 — follow-ups & reminders, the anti-"someone's memory" system
 * (Seth 2026-06-10: SoonCard promise made real). v1 = manual follow-ups with
 * due dates, snooze, complete-with-attribution. The `source` column is the
 * seam for auto-generation (agreement renewals, warranty expirations,
 * consumables, AI nudges) — those arrive as rows from edge functions later,
 * and this same UI surfaces them.
 *
 * Competitor frame: ServiceTitan's Follow-Ups inbox + HubSpot tasks. Adopted:
 * due dates, snooze, who-completed-when. Rejected for v1: per-user assignment
 * and queues — one small office, one shared list (revisit at scale).
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/lib/supabase";

export interface FollowUp {
  id: string;
  custno: string;
  locno: string | null;
  title: string;
  details: string | null;
  due_date: string;
  source: "manual" | "agreement" | "warranty" | "consumable" | "ai";
  status: "open" | "done" | "dismissed";
  snoozed_until: string | null;
  /** FOLLOWUP-02a: null = one-off; N = completing this spawns the next one
   *  due N months later (anchored to the due date; rolls from today if that
   *  would land in the past). HubSpot-style spawn-on-complete — never a wall
   *  of pre-created future rows. */
  repeat_months: number | null;
  created_by: string | null;
  created_at: string;
  completed_by: string | null;
  completed_at: string | null;
  /** Origin pointer for generated follow-ups — agreement ones carry
   *  "agr:<agrmt_no>:<expire_date>" (RENEWAL-LOOP-02 reconciles on it). */
  source_ref?: string | null;
  /** FOLLOW-UP-01 (mobile): the tech who wants to handle it himself — it
   *  sits on his phone until cleared; null = the office's shared list. */
  assigned_empno?: string | null;
  /** The call it was written at (ESC dispatch # or Pegasus ticket id). */
  origin_dispatch?: string | null;
  /** The tech (empno) who wrote it from the phone. */
  created_empno?: string | null;
}

/** A due date that lands on a weekend rolls to Monday — nothing comes due on
 *  a closed day, and nothing waits past it either (it stays until cleared). */
export function rollToWeekday(iso: string): string {
  const [y, m, d] = iso.split("-").map(Number);
  const dt = new Date(y, m - 1, d);
  const dow = dt.getDay();
  if (dow === 6) dt.setDate(dt.getDate() + 2);
  else if (dow === 0) dt.setDate(dt.getDate() + 1);
  return `${dt.getFullYear()}-${String(dt.getMonth() + 1).padStart(2, "0")}-${String(dt.getDate()).padStart(2, "0")}`;
}

/** ISO date N months from today (same day-of-month, clamped by JS Date). */
export function monthsFromNowISO(months: number): string {
  const d = new Date();
  const dt = new Date(d.getFullYear(), d.getMonth() + months, d.getDate());
  return `${dt.getFullYear()}-${String(dt.getMonth() + 1).padStart(2, "0")}-${String(dt.getDate()).padStart(2, "0")}`;
}

/** "Next spring" = the next Apr 1, "next fall" = the next Sep 15 — at least
 *  60 days out, so "next fall" said in September means next year's. */
export function nextSeasonISO(kind: "spring" | "fall"): string {
  const today = new Date();
  const month = kind === "spring" ? 3 : 8; // 0-based: April / September
  const day = kind === "spring" ? 1 : 15;
  let dt = new Date(today.getFullYear(), month, day);
  while ((dt.getTime() - today.getTime()) / 86400000 < 60) dt = new Date(dt.getFullYear() + 1, month, day);
  return `${dt.getFullYear()}-${String(dt.getMonth() + 1).padStart(2, "0")}-${String(dt.getDate()).padStart(2, "0")}`;
}

/** Next due date for a recurring follow-up: due + N months, rolled forward
 *  from today when the chain is so overdue the next slot already passed. */
export function nextDueDate(dueISO: string, months: number): string {
  const addMonths = (iso: string) => {
    const [y, m, d] = iso.split("-").map(Number);
    const dt = new Date(y, m - 1 + months, d);
    return `${dt.getFullYear()}-${String(dt.getMonth() + 1).padStart(2, "0")}-${String(dt.getDate()).padStart(2, "0")}`;
  };
  let next = addMonths(dueISO);
  if (next <= todayISO()) next = addMonths(todayISO());
  return next;
}

export function todayISO(): string {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

/** A customer's OPEN follow-ups (the 360 card). Snoozed ones stay listed with
 *  their wake date — hiding them entirely is how promises get lost. */
export function useCustomerFollowups(custno: string | undefined) {
  return useQuery({
    queryKey: ["followups", custno],
    enabled: !!custno,
    staleTime: 30 * 1000,
    queryFn: async (): Promise<FollowUp[]> => {
      const { data, error } = await supabase
        .from("pegasus_followup")
        .select("*")
        .eq("custno", custno as string)
        .eq("status", "open")
        .order("due_date", { ascending: true });
      if (error) throw error;
      return (data ?? []) as FollowUp[];
    },
  });
}

/** Due/overdue across ALL customers (the Today panel): open, due on or before
 *  today, and not snoozed past today. */
export function useDueFollowups() {
  return useQuery({
    queryKey: ["followups_due"],
    staleTime: 60 * 1000,
    refetchInterval: 5 * 60 * 1000,
    queryFn: async (): Promise<FollowUp[]> => {
      const today = todayISO();
      const { data, error } = await supabase
        .from("pegasus_followup")
        .select("*")
        .eq("status", "open")
        .lte("due_date", today)
        .or(`snoozed_until.is.null,snoozed_until.lte.${today}`)
        .order("due_date", { ascending: true })
        // 300, not 50: the auto-created agreement renewals (100+ open at a
        // time) were filling the cap and could push a manual follow-up due
        // today off the list entirely (found 2026-09-02).
        .limit(300);
      if (error) throw error;
      return (data ?? []) as FollowUp[];
    },
  });
}

function useInvalidate() {
  const qc = useQueryClient();
  return () => {
    qc.invalidateQueries({ queryKey: ["followups_due"] });
    qc.invalidateQueries({ queryKey: ["followups"] });
    qc.invalidateQueries({ queryKey: ["followups_tech"] });
    qc.invalidateQueries({ queryKey: ["customer_signals"] });
  };
}

export function useAddFollowup() {
  const invalidate = useInvalidate();
  return useMutation({
    mutationFn: async (v: {
      custno: string;
      locno?: string | null;
      title: string;
      details?: string | null;
      due_date: string;
      repeat_months?: number | null;
      assigned_empno?: string | null;
      origin_dispatch?: string | null;
      created_empno?: string | null;
    }) => {
      const { data: u } = await supabase.auth.getUser();
      const { error } = await supabase.from("pegasus_followup").insert({
        custno: v.custno,
        locno: v.locno ?? null,
        title: v.title.trim(),
        details: v.details?.trim() || null,
        due_date: v.due_date,
        repeat_months: v.repeat_months ?? null,
        source: "manual",
        created_by: u?.user?.email ?? null,
        assigned_empno: v.assigned_empno ?? null,
        origin_dispatch: v.origin_dispatch ?? null,
        created_empno: v.created_empno ?? null,
      });
      if (error) throw error;
    },
    onSuccess: () => invalidate(),
  });
}

export function useCompleteFollowup() {
  const invalidate = useInvalidate();
  return useMutation({
    /** Pass the whole row so a recurring one can spawn its successor. */
    mutationFn: async (f: FollowUp) => {
      const { data: u } = await supabase.auth.getUser();
      const { error } = await supabase
        .from("pegasus_followup")
        .update({
          status: "done",
          completed_by: u?.user?.email ?? null,
          completed_at: new Date().toISOString(),
          updated_at: new Date().toISOString(),
        })
        .eq("id", f.id)
        // Idempotency: only the first completer spawns the successor.
        .eq("status", "open");
      if (error) throw error;

      // FOLLOWUP-02a: recurrence = spawn-on-complete (HubSpot pattern).
      if (f.repeat_months) {
        const { error: e2 } = await supabase.from("pegasus_followup").insert({
          custno: f.custno,
          locno: f.locno,
          title: f.title,
          details: f.details,
          // FOLLOW-UP-01: the next one never lands on a weekend either
          due_date: rollToWeekday(nextDueDate(f.due_date, f.repeat_months)),
          repeat_months: f.repeat_months,
          source: f.source,
          created_by: u?.user?.email ?? null,
          // the recurrence keeps its owner and its origin
          assigned_empno: f.assigned_empno ?? null,
          origin_dispatch: f.origin_dispatch ?? null,
          created_empno: f.created_empno ?? null,
        });
        if (e2) throw e2;
      }
    },
    onSuccess: () => invalidate(),
  });
}

/** FOLLOW-UP-01 (mobile): a tech's own follow-ups — open, assigned to him,
 *  due within a week (overdue included) and not snoozed past that. They stay
 *  on his phone every day until he clears them; the office sees the same rows. */
export function useTechFollowups(empno: string | null | undefined) {
  return useQuery({
    queryKey: ["followups_tech", empno],
    enabled: !!empno,
    staleTime: 60 * 1000,
    refetchInterval: 5 * 60 * 1000,
    queryFn: async (): Promise<FollowUp[]> => {
      const horizon = daysFromNowISO(7);
      const { data, error } = await supabase
        .from("pegasus_followup")
        .select("*")
        .eq("status", "open")
        .eq("assigned_empno", empno as string)
        .lte("due_date", horizon)
        .or(`snoozed_until.is.null,snoozed_until.lte.${horizon}`)
        .order("due_date", { ascending: true })
        .limit(100);
      if (error) throw error;
      return (data ?? []) as FollowUp[];
    },
  });
}

/** The tech hands one to the office's shared list (or the office assigns one to a tech). */
export function useAssignFollowup() {
  const invalidate = useInvalidate();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (v: { id: string; assigned_empno: string | null }) => {
      const { error } = await supabase
        .from("pegasus_followup")
        .update({ assigned_empno: v.assigned_empno, updated_at: new Date().toISOString() })
        .eq("id", v.id);
      if (error) throw error;
    },
    onSuccess: () => { invalidate(); qc.invalidateQueries({ queryKey: ["followups_tech"] }); },
  });
}

/** Snooze = "not now, wake me later." Keeps due_date (the real promise date);
 *  snoozed_until governs when it reappears as due. */
export function useSnoozeFollowup() {
  const invalidate = useInvalidate();
  return useMutation({
    mutationFn: async (v: { id: string; until: string }) => {
      const { error } = await supabase
        .from("pegasus_followup")
        .update({ snoozed_until: v.until, updated_at: new Date().toISOString() })
        .eq("id", v.id);
      if (error) throw error;
    },
    onSuccess: () => invalidate(),
  });
}

/** ISO date N days from today (snooze quick-picks). */
export function daysFromNowISO(days: number): string {
  const d = new Date();
  d.setDate(d.getDate() + days);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

/** Plain-English due chip text + tone. */
export function dueLabel(f: FollowUp): { text: string; tone: "overdue" | "today" | "future" | "snoozed" } {
  const today = todayISO();
  if (f.snoozed_until && f.snoozed_until > today)
    return { text: `snoozed until ${f.snoozed_until.slice(5).replace("-", "/")}`, tone: "snoozed" };
  if (f.due_date < today) {
    const days = Math.round(
      (new Date(today).getTime() - new Date(f.due_date).getTime()) / 86400000,
    );
    return { text: days === 1 ? "1 day overdue" : `${days} days overdue`, tone: "overdue" };
  }
  if (f.due_date === today) return { text: "due today", tone: "today" };
  return { text: `due ${f.due_date.slice(5).replace("-", "/")}`, tone: "future" };
}
